import LayoutLeft from "@redotech/redo-icons/layout-left.svg";
import LayoutLeftFilled from "@redotech/redo-icons/layout-left_filled.svg";
import PhoneIcon from "@redotech/redo-icons/phone.svg";
import PhoneFilledIcon from "@redotech/redo-icons/phone_filled.svg";
import GearIcon from "@redotech/redo-icons/settings-02.svg";
import GearFilledIcon from "@redotech/redo-icons/settings-02_filled.svg";
import classNames from "classnames";
import { JSXElementConstructor, memo } from "react";
import { Flex } from "../../../flex";
import * as darkModeCss from "../../../theme/component-colors-dark.module.css";
import { usePhoneWidget } from "../../../voice/phone-widget-context";
import { RedoNavigationTeamItem } from "../common/redo-nav-team-item";
import { UserProfile } from "../common/redo-nav-user-profile";
import * as navigationCss from "../redo-navigation.module.css";
import { PrimaryNavigationTab } from "./redo-primary-nav-tab";

interface BasePrimaryTab {
  id: string;
  hasNotification: boolean;
  UnfilledIcon: JSXElementConstructor<any>;
  FilledIcon: JSXElementConstructor<any>;
  friendlyName: string;
}

export interface LinkPrimaryTab extends BasePrimaryTab {
  type: "link";
  href: string;
  pathRoot: string;
}

interface ButtonPrimaryTab extends BasePrimaryTab {
  type: "button";
  onClick: () => void;
}

export type PrimaryTab = LinkPrimaryTab | ButtonPrimaryTab;

interface RedoPrimaryNavProps {
  toggleCollapseMode: () => void;
  isCollapseMode: boolean;
  enterSettingsUrl: string | null;
  settingsRoot: string;
  pageTabs: LinkPrimaryTab[];
  hideTeamSwitcher?: boolean;
}

export const RedoPrimaryNav = memo(function RedoPrimaryNav({
  toggleCollapseMode,
  isCollapseMode,
  enterSettingsUrl,
  settingsRoot,
  pageTabs,
  hideTeamSwitcher = false,
}: RedoPrimaryNavProps) {
  const { isVisible, toggleVisibility, isVoiceAvailable } = usePhoneWidget();

  return (
    <Flex
      align="center"
      bgColor="tertiary"
      className={classNames(darkModeCss.darkMode, navigationCss.primaryNav)}
      dir="column"
      grow={1}
      justify="space-between"
      px="md"
      py="xl"
    >
      <Flex align="center" dir="column" gap="lg">
        {!hideTeamSwitcher && <RedoNavigationTeamItem />}
        <Flex dir="column" overflowY="auto">
          <Flex
            dir="column"
            gap="lg"
            overflowY="auto"
            style={{ scrollbarWidth: "none" }}
          >
            {pageTabs.map((tab) => (
              <PrimaryNavigationTab key={tab.id} tab={tab} />
            ))}
          </Flex>
        </Flex>
      </Flex>
      <Flex align="center" dir="column" gap="lg">
        <PrimaryNavigationTab
          manuallySelected={isCollapseMode}
          tab={{
            id: "collapse",
            UnfilledIcon: LayoutLeft,
            FilledIcon: LayoutLeftFilled,
            type: "button",
            friendlyName: "Collapse",
            hasNotification: false,
            onClick: toggleCollapseMode,
          }}
        />
        {isVoiceAvailable && (
          <PrimaryNavigationTab
            manuallySelected={isVisible}
            tab={{
              id: "phone",
              UnfilledIcon: PhoneIcon,
              FilledIcon: PhoneFilledIcon,
              type: "button",
              friendlyName: "Phone",
              hasNotification: false,
              onClick: toggleVisibility,
            }}
          />
        )}
        {enterSettingsUrl && (
          <PrimaryNavigationTab
            tab={{
              id: "settings",
              hasNotification: false,
              UnfilledIcon: GearIcon,
              FilledIcon: GearFilledIcon,
              type: "link",
              href: enterSettingsUrl,
              friendlyName: "Settings",
              pathRoot: settingsRoot,
            }}
          />
        )}
        <UserProfile collapsed />
      </Flex>
    </Flex>
  );
});
